import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { debounceTime, Subject, takeUntil } from 'rxjs';

import { MatSelectModule } from '@angular/material/select';
import {
  IPaginationValue,
  PaginationComponent,
} from '../../../shared/components/pagination/pagination.component';
import { TableComponent } from '../../../shared/components/table/table.component';
import { LoadingService } from '../../../shared/services/loading.service';
import { PollService } from '../../../shared/services/poll.service';
import { ReportFilterService } from '../../../shared/services/report-filter.service';
import { ITableHeader } from '../../../utils/interfaces/table-header';
import { IPollToList } from '../../../utils/interfaces/poll';
import { PollTypeDescriptionPipe } from '../../../shared/pipes/poll-type-description.pipe';


@Component({
  selector: 'app-list-of-reports',
  standalone: true,
  imports: [
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    TableComponent,
    PaginationComponent,
    MatSelectModule,
  ],
  templateUrl: './list-of-reports.component.html',
  styleUrl: './list-of-reports.component.scss',
})
export class ListOfReportsComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly loadingService = inject(LoadingService);
  private readonly pollService = inject(PollService);
  private readonly reportFilterService = inject(ReportFilterService);

  private readonly unsubscribe$ = new Subject();

  tableHeader: ITableHeader[] = [];

  list: IPollToList[] = [];

  countPoll = 0;
  pagination: IPaginationValue = {
    page: 1,
    take: 10,
  };

  searchString = new FormControl<string>('');
  searchStatus = new FormControl<boolean | null>(null);
  actionList: string[] = ['Visualizar'];

  ngOnInit(): void {
    this.initData();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  initData(): void {
    this.tableHeader = [
      'Status,status',
      'Pesquisas,title',
      'Tipo de pesquisas,typeDescription',
      'Empresa,company',
      'N respostas,submissionCount',
      'Última avaliação,lastSubmission',
      'Ações',
    ].map((item) => {
      const [description, propertyKey] = item.split(',');

      return {
        description: description,
        propertyKey: propertyKey ?? '',
        status: propertyKey === 'status',
        date: propertyKey === 'lastSubmission',
        actions: description === 'Ações',
        filter: false,
        showHour: false,
      };
    });

    const { count, data } = this.activatedRoute.snapshot.data['polls'];
    this.countPoll = count ?? 0;

    const pollTypeDescriptionPipe = new PollTypeDescriptionPipe();
    this.list = data.map((item: IPollToList) => ({
      ...item,
      typeDescription: pollTypeDescriptionPipe.transform(item.type),
    }));

    this.searchString.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .pipe(debounceTime(700))
      .subscribe(() => {
        this.loadingService.showLoading();
        this.getPolls(true);
      });

    this.searchStatus.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.loadingService.showLoading();
        this.getPolls(true);
      });

    this.reportFilterService.setFilter(null);
  }

  onPaginationChange(event: IPaginationValue): void {
    this.pagination = event;

    this.loadingService.showLoading();
    this.getPolls();
  }

  getPolls(search = false): void {
    this.pollService
      .getPolls(
        search,
        this.searchString.value ?? '',
        this.pagination.page,
        this.pagination.take,
        this.searchStatus.value
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        const pollTypeDescriptionPipe = new PollTypeDescriptionPipe();
        this.list = res.data.map((item: IPollToList) => ({
          ...item,
          typeDescription: pollTypeDescriptionPipe.transform(item.type),
        }));
        this.countPoll = res.count ?? 0;
        if (search) {
          this.pagination.page = 1;
        }
        this.loadingService.closeLoading();
      });
  }

  selectAction(item: { id: string; func: string }) {
    if (item.func === 'Visualizar') {
      this.router.navigate(['./main/report/data', item.id]);
    }
  }
}
